import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../../../context/WorkspaceContext";

/**
 * Shared state for the interactive API docs experience.
 *
 * Exposes:
 *   - apiKey / setApiKey       current x-api-key header value
 *   - selectedKeyId / setKeyId which stored key is active (or null for custom)
 *   - keys                     array of { id, key, name, ... } from /api/settings/api-keys
 *   - sessions                 array from /api/sessions (each has sessionId)
 *   - selectedSessionId / setSessionId
 *   - workspaceId              active workspace/client id for scoped API calls
 *   - baseUrl                  API origin, respects REACT_APP_API_BASE when set
 *   - loading / error          async state flags for initial fetch
 *   - refresh()                refetch both lists
 *
 * The chosen key + sessionId are persisted to localStorage so Try-It defaults
 * survive page reloads.
 */

const LS_API_KEY = "walytic.docs.apiKey";
const LS_KEY_ID = "walytic.docs.keyId";
const LS_SESSION_ID = "walytic.docs.sessionId";

// Resolve the base URL once at module load. Order of precedence:
//   1. REACT_APP_API_BASE env var (CRA convention — matches src/utils/api.js)
//   2. Hardcoded production default
const resolvedBaseUrl = (() => {
  const fromEnv =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE) || "";
  return (fromEnv || "https://api.walytic.com").replace(/\/+$/, "");
})();

export const DocsCtx = createContext(null);

function readLS(key, fallback = "") {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function writeLS(key, value) {
  try {
    if (value === null || value === undefined || value === "") localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch {
    /* ignore quota / privacy-mode */
  }
}

export function DocsProvider({ children }) {
  const { activeWorkspace } = useWorkspace() || {};
  const workspaceId = activeWorkspace?.clientId || activeWorkspace?.id || readLS("activeWorkspaceId", "");
  const [apiKey, setApiKeyState] = useState(() => readLS(LS_API_KEY, ""));
  const [selectedKeyId, setSelectedKeyIdState] = useState(() => readLS(LS_KEY_ID, ""));
  const [selectedSessionId, setSelectedSessionIdState] = useState(() =>
    readLS(LS_SESSION_ID, "")
  );
  const [keys, setKeys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setApiKey = useCallback((v) => {
    setApiKeyState(v || "");
    writeLS(LS_API_KEY, v || "");
  }, []);

  const setSelectedKeyId = useCallback(
    (id, keysList) => {
      setSelectedKeyIdState(id || "");
      writeLS(LS_KEY_ID, id || "");
      // When picking a stored key, mirror its value into apiKey too.
      const list = keysList || keys;
      const match = list.find((k) => String(k.id) === String(id));
      if (match && match.key) setApiKey(match.key);
    },
    [keys, setApiKey]
  );

  const setSelectedSessionId = useCallback((v) => {
    setSelectedSessionIdState(v || "");
    writeLS(LS_SESSION_ID, v || "");
  }, []);

  const fetchJson = useCallback(
    async (path) => {
      const res = await fetch(`${resolvedBaseUrl}${path}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(workspaceId
            ? { "X-Workspace-Id": workspaceId, "X-Client-Id": workspaceId }
            : {}),
        },
      });
      if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
      const body = await res.json().catch(() => null);
      // Endpoints sometimes wrap in { success, data } — unwrap if so.
      if (body && typeof body === "object" && Array.isArray(body.data)) return body.data;
      return body;
    },
    [workspaceId]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [k, s] = await Promise.all([
        fetchJson("/api/settings/api-keys").catch(() => []),
        fetchJson("/api/sessions").catch(() => []),
      ]);

      const keysList = Array.isArray(k) ? k : [];
      const sessionList = Array.isArray(s) ? s : [];
      setKeys(keysList);
      setSessions(sessionList);

      // Restore previously-selected key/session from localStorage (if still valid).
      // Never auto-select — the user must explicitly pick from the dropdown.
      const storedKeyId = readLS(LS_KEY_ID, "");
      if (storedKeyId) {
        const match = keysList.find((x) => String(x.id) === String(storedKeyId));
        if (match && match.key) setApiKey(match.key);
      }

    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, setApiKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      apiKey,
      setApiKey,
      selectedKeyId,
      setSelectedKeyId,
      keys,
      sessions,
      selectedSessionId,
      setSessionId: setSelectedSessionId,
      setSelectedSessionId,
      activeWorkspace,
      workspaceId,
      baseUrl: resolvedBaseUrl,
      loading,
      error,
      refresh,
    }),
    [
      apiKey,
      setApiKey,
      selectedKeyId,
      setSelectedKeyId,
      keys,
      sessions,
      selectedSessionId,
      setSelectedSessionId,
      activeWorkspace,
      workspaceId,
      loading,
      error,
      refresh,
    ]
  );

  return <DocsCtx.Provider value={value}>{children}</DocsCtx.Provider>;
}

export function useDocsCtx() {
  const ctx = useContext(DocsCtx);
  // Return a safe stub when used outside the provider (e.g. static MDX usage).
  if (!ctx) {
    return {
      apiKey: "",
      setApiKey: () => {},
      selectedKeyId: "",
      setSelectedKeyId: () => {},
      keys: [],
      sessions: [],
      selectedSessionId: "",
      setSessionId: () => {},
      setSelectedSessionId: () => {},
      activeWorkspace: null,
      workspaceId: "",
      baseUrl: resolvedBaseUrl,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  return ctx;
}

export default DocsProvider;
